import React, { FC, useState } from "react";
import { Link } from "react-router-dom";
import { Share, ThreeDots } from "react-bootstrap-icons";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ImageProject from "../../projects/projectsList/img/project.png";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import { UserIcon } from "../../shadedPages/UserIcon/UserIcon";
import PageActions from "../../projects/projectPage/pageActions";
import {
  SharedDomainsQuery,
  useSharedDomainsQuery,
} from "../../../graphql/access.hooks";
import Pagination from "../../../components/pagination";
import NoShared from "../NoShared";
import DeleteProjectModal from "../../../components/modals/deleteProjectModal";
import { useDomainDeleteMutation } from "../../../graphql/projects.hooks";

type ProjectListItemProps = {
  item: SharedDomainsQuery["sharedDomains"]["domains"][number];
  handleShowDeleteModal: (
    project: SharedDomainsQuery["sharedDomains"]["domains"][number]
  ) => void;
  isLastProject: boolean;
};

type Props = {
  searchTerm: string;
};

export const ProjectsTable = ({ searchTerm }: Props) => {
  const { t } = useTranslation();

  const [selectedProject, setSelectedProject] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(12);

  const { data: sharedDomainsData, refetch } = useSharedDomainsQuery({
    variables: { page: currentPage, limit: limit, search: searchTerm },
  });
  const [deleteProject] = useDomainDeleteMutation({
    onCompleted: () => {
      setTimeout(() => {
        refetch({
          page: 1,
          limit: limit,
          search: searchTerm,
        });
      }, 1000);
    },
  });

  const sharedDomains = sharedDomainsData?.sharedDomains?.domains;

  const pagination = sharedDomainsData?.sharedDomains?.paginator;
  const pageCount = pagination?.totalPages || 0;
  const totalItems = pagination?.totalItemCount || 0;
  const firstItemIndex = pagination
    ? (currentPage - 1) * pagination.itemNumberPerPage + 1
    : 0;
  const lastItemIndex = pagination
    ? Math.min(currentPage * pagination.itemNumberPerPage, totalItems)
    : 0;

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = Number(event.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
    refetch({
      page: 1,
      limit: newLimit,
      search: searchTerm,
    });
  };

  const handleShowDeleteModal = (
    project: SharedDomainsQuery["sharedDomains"]["domains"][number]
  ) => {
    setSelectedProject(project);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setSelectedProject(null);
    setShowDeleteModal(false);
  };

  const handleDeleteProject = async () => {
    if (selectedProject) {
      const domainId = selectedProject?.domain
        ? selectedProject?.domain?.id
        : selectedProject.id;
      await deleteProject({ variables: { domainId: domainId } });
    }
    handleCloseDeleteModal();
  };

  return (
    <>
      {sharedDomains?.length ? (
        <>
          <div className="col-12 table-scroll">
            <table className="table table-index mb-0 overflow-hidden rounded">
              <thead>
                <tr className="bg-disabled table-heading">
                  <th className="text-dark-gray py-3 ps-4 col-1">
                    {t("projects.tablePreview")}
                  </th>
                  <th className="text-dark-gray py-3 col-3">
                    {t("projects.tableTitle")}
                  </th>
                  <th className="text-dark-gray py-3 col-1 text-nowrap text-capitalize">
                    {t("share.tableShareDate")}
                  </th>
                  <th className="text-dark-gray py-3 col-2">
                    {t("projects.tableUsers")}
                  </th>
                  <th className="text-dark-gray py-3 col-1">
                    {t("projects.tableActions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white table-content border-white">
                {sharedDomains?.map(
                  (
                    project: SharedDomainsQuery["sharedDomains"]["domains"][number],
                    index: number
                  ) => (
                    <tr className="align-middle" key={project.id}>
                      <ProjectItem
                        item={project}
                        handleShowDeleteModal={handleShowDeleteModal}
                        isLastProject={sharedDomains.length === index + 1}
                      />
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            t={t}
            pages={pageCount}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            firstItemIndex={firstItemIndex}
            lastItemIndex={lastItemIndex}
            totalItems={totalItems}
            limit={limit}
            onLimitChange={handleLimitChange}
          />
        </>
      ) : (
        <NoShared />
      )}
      <DeleteProjectModal
        title={selectedProject?.title || ""}
        showModal={showDeleteModal}
        handleCloseModal={handleCloseDeleteModal}
        onDelete={handleDeleteProject}
      />
    </>
  );
};

export const ProjectItem: FC<ProjectListItemProps> = ({
  item,
  handleShowDeleteModal,
  isLastProject,
}) => {
  const lng = useGetUrlLocale();

  return (
    <>
      <td className="py-3 ps-4">
        <Link
          to={`${lng}/projects/${item.id}`}
          className={`page-prew-table d-flex justify-content-center overflow-hidden border rounded`}
        >
          <img
            className="h-100"
            src={item?.firstPage?.image || ImageProject}
            alt={item.title}
          />
        </Link>
      </td>
      <td>
        <p className="table-title text-nowrap m-0">
          {item.title.length > 20
            ? `${item.title.slice(0, 20)}...`
            : item.title}
        </p>
      </td>
      <td className="text-nowrap">
        {item?.userDomainAccess?.createdAt.slice(0, 10)}
      </td>
      <td>
        <div className="d-flex flex-row-reverse justify-content-end">
          {item?.userAccesses?.map(({ email }: { email: string }) => (
            <UserIcon key={email} email={email} />
          ))}
          <UserIcon email={item?.user?.email} className="user-icon-owner" />
        </div>
      </td>
      <td>
        <div className="d-flex align-items-center actions-bar">
          <div>
            <Link
              to={`${lng}/user/access/domain/${item.id}/access`}
              className="btn btn-outline-primary primary-hover rounded-1 settings-icon text-blue"
            >
              <Share className="fs-6" />
            </Link>
          </div>
          <Dropdown
            drop={isLastProject ? "up" : "down"}
            className="pe-3 mx-3 dropup"
          >
            <Dropdown.Toggle
              variant="outline-primary"
              id="dropdown-basic"
              className="primary-hover"
            >
              <ThreeDots className="fs-6" />
            </Dropdown.Toggle>
            <PageActions
              type={"domain"}
              domainId={item.id}
              accessType={item?.userDomainAccess?.accessType}
              deleteDomain={handleShowDeleteModal}
            />
          </Dropdown>
        </div>
      </td>
    </>
  );
};
